import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { sortBy } from 'lodash';
import { array, func, number, object, oneOfType, string } from 'prop-types';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { I18n } from 'react-redux-i18n';
import styled from 'styled-components';
import * as Yup from 'yup';
import { addRespondent } from '../../../api/respondents';
import { addRespondentSuccess } from '../../../store/respondents/actions';
import createToastNotification from '../../../utils/createToastNotification';
import isChineseEnv from '../../../utils/isChineseEnv';
import GroupsTab from './GroupsTab';
import TagsTab from './TagsTab';

import CustomButton from '../../reusable/Buttons/Button';
import DateSelect from '../../reusable/FormComponents/DateSelect';
import CustomSelect from '../../reusable/Selects/Select';

import { errorMessages } from '../../../constants/errorMessages';
import {
  educationOptions,
  ethnicityOptions,
  genderOptions,
  industryOptions,
  languageOptions,
  occupationOptions,
  sectorOptions,
} from '../../../constants/respondentSelectOptions';
import FormikInput from '../../reusable/FormComponents/FormikInput';

const optionsMapper = {
  Ethnicity: ethnicityOptions,
  Education: educationOptions,
  'Job Area': occupationOptions,
  Industry: industryOptions,
  Language: languageOptions,
  Sector: sectorOptions,
};

const selectorMapper = {
  Ethnicity: 'ethnicity',
  Education: 'education',
  'Job Area': 'jobArea',
  Industry: 'industry',
  Language: 'language',
  Sector: 'sector',
};

const defaultBiodataTemplate = [
  {
    name: 'ethnicity',
    label: 'Ethnicity',
    optionValues: optionsMapper.Ethnicity,
  },
  {
    name: 'education',
    label: 'Education',
    optionValues: optionsMapper.Education,
  },
  {
    name: 'jobArea',
    label: 'Job Area',
    optionValues: optionsMapper['Job Area'],
  },
  {
    name: 'sector',
    label: 'Sector',
    optionValues: optionsMapper.Sector,
  },
  {
    name: 'industry',
    label: 'Industry',
    optionValues: optionsMapper.Industry,
  },
  {
    name: 'language',
    label: 'Language',
    optionValues: optionsMapper.Language,
  },
];

// Job Area options are grouped ({ label, options }), so flatten before looking up a value
const findOption = (options, value) =>
  options.flatMap((item) => item.options || item).find((item) => item.value === value) || null;

const allOccupationOptions = occupationOptions.reduce((acc, item) => {
  return [...acc, ...item.options];
}, []);

const validationSchema = Yup.object().shape({
  firstName: Yup.string()
    .trim()
    .required(errorMessages.inputField)
    .max(200, errorMessages.tooLong(200)),
  ...(isChineseEnv
    ? {
        familyName: Yup.string()
          .trim()
          .required(errorMessages.inputField)
          .max(200, errorMessages.tooLong(200))
          .optional(),
      }
    : {
        familyName: Yup.string()
          .trim()
          .required(errorMessages.inputField)
          .max(200, errorMessages.tooLong(200)),
      }),
  email: Yup.string()
    .trim()
    .email(errorMessages.emailField)
    .max(150, errorMessages.tooLong(150)),
  title: Yup.string().max(50, errorMessages.tooLong(50)),
  reference: Yup.string().max(150, errorMessages.tooLong(150)),
  company: Yup.string().max(150, errorMessages.tooLong(150)),
});

const AddRespondentForm = ({ allTags, allGroups, activeTab, groupId, onSubmit }) => {
  const dispatch = useDispatch();

  const availableBiodataTemplates = useSelector((state) => state.user.biotemplates);
  const hasDefaultTemplate = availableBiodataTemplates.find(
    (template) => template.isDefault === true && template.value !== 0,
  );
  const biodataTemplate = hasDefaultTemplate || availableBiodataTemplates[0];

  const mappedBiodataTemplates = [];
  if (biodataTemplate) {
    biodataTemplate.bioOptions.forEach(({ label, values }) => {
      mappedBiodataTemplates.push({
        label: selectorMapper[label],
        options: optionsMapper[label],
        values,
      });
    });
  }
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: addRespondent,
    onSuccess: (newRespondent) => {
      createToastNotification({ message: I18n.t('respondentCreationToast') });
      queryClient.setQueryData(['respondents'], (oldData) => {
        const propertyDiff = {
          doneTests: [],
          assessmentsDone: [],
          isReportsAvailable: false,
        };
        // eslint-disable-next-line no-param-reassign
        newRespondent = { ...propertyDiff, ...newRespondent };
        if (!oldData || !oldData.respondents) {
          return { respondents: [newRespondent] };
        }
        return {
          ...oldData,
          respondents: [newRespondent, ...oldData.respondents],
        };
      });
      dispatch(addRespondentSuccess());
    },
    onError: (err) => {
      console.error('Error adding respondent:', err);
    },
  });

  // Use Formik's hook instead of withFormik
  const formik = useFormik({
    initialValues: {
      firstName: '',
      familyName: '',
      sex: 'U',
      email: '',
      title: '',
      dob: null,
      reference: '',
      company: '',
      education: '',
      ethnicity: '',
      jobArea: '',
      sector: '',
      industry: '',
      language: '',
      tags: [],
      groups: [],
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      const preparedData = {
        ...values,
        tags: values.tags.map((item) => item.name),
        groups: values.groups.map((item) => item.groupID),
        firstLanguage: values.language,
      };
      mutation.mutate(preparedData);
      if (onSubmit) onSubmit();
    },
  });

  const textInputHandler = (name) => (e) => {
    if (name === 'email') {
      e.target.value = e.target.value.trim();
    }
    formik.setFieldError(name, '');
    formik.handleChange(e);
  };

  const renderTextInput = useCallback(
    (name, label) => (
      <StyledInput
        name={name}
        id={name}
        inputName={label}
        onChange={textInputHandler(name)}
        value={formik.values[name]}
        errors={formik.errors}
        touched={formik.touched}
      />
    ),
    [formik.values, formik.errors, formik.touched],
  );

  // TAGS TAB MANAGEMENT
  const allTagsOptions = allTags.filter((item) => !formik.values.tags.find((tag) => tag.tagID === item.tagID));
  const tagOptions = sortBy(
    allTagsOptions.map((item) => ({ ...item, value: item.tagID, label: item.name })),
    'name',
  );

  const addTag = (newTags) => {
    const newTagsWithName = newTags.map((item) => ({ ...item, name: item.name || item.value }));
    const withoutDuplicates = newTagsWithName.filter(
      (item) => !formik.values.tags.find((tag) => tag.name === item.name),
    );
    formik.setFieldValue('tags', [...formik.values.tags, ...withoutDuplicates]);
  };

  const removeTag = (tag) => {
    formik.setFieldValue(
      'tags',
      formik.values.tags.filter((item) => item.tagID !== tag.tagID),
    );
  };

  // GROUPS TAB MANAGEMENT
  const allGroupsOptions = allGroups.filter(
    (item) => !formik.values.groups.find((group) => group.groupID === item.groupID),
  );
  const groupsOptions = sortBy(
    allGroupsOptions.map((item) => ({ ...item, value: item.groupID, label: item.name })),
    'name',
  );

  useEffect(() => {
    if (groupId && groupsOptions.length && !formik.values.groups.length) {
      formik.setFieldValue(
        'groups',
        groupsOptions.filter((item) => item.groupID === Number(groupId)),
      );
    }
  }, [groupsOptions.length, groupId]); // eslint-disable-line react-hooks/exhaustive-deps

  const addGroup = (newGroups) => {
    const newGroupsWithName = newGroups.map((item) => ({ ...item, name: item.name || item.value }));
    const withoutDuplicates = newGroupsWithName.filter(
      (item) => !formik.values.groups.find((group) => group.name === item.name),
    );
    formik.setFieldValue('groups', [...formik.values.groups, ...withoutDuplicates]);
  };

  const removeGroup = (group) => {
    formik.setFieldValue(
      'groups',
      formik.values.groups.filter((item) => item.groupID !== group.groupID),
    );
  };

  const formatBioData = (biodataCategories) => {
    const biodataFormatedCategories = [];
    biodataCategories.forEach(({ label, options, values }) => {
      if (values[0] === '') return;
      const categoryValue = [];
      values.forEach((item) => {
        const finalOptions = label === 'jobArea' ? allOccupationOptions : options;
        finalOptions.forEach(({ label, value }) => {
          if (item === value) {
            categoryValue.push({ label, value });
          }
        });
      });
      biodataFormatedCategories.push({ name: label, optionValues: categoryValue });
    });
    return biodataFormatedCategories;
  };

  const biodataFormatedOptions = biodataTemplate ? formatBioData(mappedBiodataTemplates) : defaultBiodataTemplate;

  return (
    <Form onSubmit={formik.handleSubmit} autoComplete="off">
      {activeTab.name === 'mainDetails' && (
        <InputsHolder>
          {renderTextInput('firstName', I18n.t('First Name'))}
          {!isChineseEnv && renderTextInput('familyName', I18n.t('Family Name'))}
          <GenderSelect
            placeholder={I18n.t('Sex')}
            options={genderOptions}
            selectProps={{
              onChange: (option) => formik.setFieldValue('sex', option.value),
              value: genderOptions.find((item) => item.value === formik.values.sex) || null,
              menuPosition: 'fixed',
            }}
          />
          {renderTextInput('title', I18n.t('BioTitle'))}
          {renderTextInput('email', I18n.t('Email Address'))}
          <DatePicker
            selectedDate={formik.values.dob}
            onDateChange={(date) => formik.setFieldValue('dob', date)}
            label={I18n.t('Date of Birth')}
          />
          {renderTextInput('reference', I18n.t('Reference'))}
        </InputsHolder>
      )}
      {activeTab.name === 'extraDetails' && (
        <InputsHolder>
          {renderTextInput('company', I18n.t('Company Name'))}
          {biodataFormatedOptions.map(({ name, label, optionValues }) => (
            <StyledSelect
              key={name}
              placeholder={label || name}
              options={optionValues}
              selectProps={{
                onChange: (option) => formik.setFieldValue(name, option ? option.value : ''),
                value: findOption(optionValues, formik.values[name]),
                menuPosition: 'fixed',
                isClearable: true,
              }}
            />
          ))}
        </InputsHolder>
      )}
      {activeTab.name === 'tags' && (
        <TagsTab allTags={tagOptions} onAddTag={addTag} selectedTags={formik.values.tags} onRemoveTag={removeTag} />
      )}
      {activeTab.name === 'groups' && (
        <GroupsTab
          allGroups={groupsOptions}
          selectedGroups={formik.values.groups}
          onAddGroup={addGroup}
          onRemoveGroup={removeGroup}
        />
      )}
      <ButtonWrapper>
        <CustomButton type="submit">{I18n.t('Save')}</CustomButton>
      </ButtonWrapper>
    </Form>
  );
};

AddRespondentForm.propTypes = {
  allTags: array.isRequired,
  allGroups: array.isRequired,
  activeTab: object.isRequired,
  groupId: oneOfType([string, number]),
  onSubmit: func,
};

AddRespondentForm.defaultProps = {
  groupId: '',
  onSubmit: () => {},
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const InputsHolder = styled.div`
  flex-grow: 1;
`;

const StyledSelect = styled(CustomSelect)`
  margin-bottom: 2rem;
`;

const GenderSelect = styled(StyledSelect)`
  width: 8rem;
`;

const StyledInput = styled(FormikInput)`
  margin-bottom: 2rem;
`;

const DatePicker = styled(DateSelect)`
  margin-bottom: 2rem;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

export default AddRespondentForm;
